// UnitDefaultHandlersEditDialog - MODULE
// ***********************************************************************************************************************
import { Box, Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { ConfirmationDialogResult, DefaultHandlerType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import { SaveDataObj } from "../../../../framework/saveData";
import { IUnitDefaultHandlersNode } from "../../../../models/unit/unitDefaultHandlersNode";
import { IUnitDefaultHandlersNodeUser, UnitDefaultHandlersNodeUser } from "../../../../models/unit/unitDefaultHandlersNodeUser";
import { IUserListItem } from "../../../../models/user/userListItem";
import { UserListItems } from "../../../../models/user/userListItems";
import { ActionContainer } from "../../../framework/ActionContainer";
import { Dialog } from "../../../framework/Dialog/Dialog";
import { GridRow } from "../../../framework/Grid/GridRow";
import { List } from "../../../framework/List/List";
import { PanelTitle } from "../../../framework/Panel/PanelTitle";
import { SelectUserDialog } from "../../../user/SelectUserDialog/SelectUserDialog";
import { AddUserToolButton } from "source/components/framework/Button/AddUserToolButton";
import { RemoveUserToolButton } from "source/components/framework/Button/RemoveUserToolButton";

//UnitDefaultHandlersEditDialog
export interface IUnitDefaultHandlersEditDialogProp {
    classes?: string;
    companyId: string;
    showHandlerTypes?: boolean;
    unitNode: IUnitDefaultHandlersNode;
    onOk: (handlers: IUnitDefaultHandlersNodeUser[], clearSubUnitHandlers: boolean, copyHandlersToAllSameLevelUnits: boolean) => void;
    onRemove: () => void;
    onCancel: () => void;
    onGetUserSelectListItems?: (companyId: string, unitId: string, handlerType: DefaultHandlerType, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean) => Promise<UserListItems>;

}

export interface IUnitDefaultHandlersEditDialogState {
    hasExplicitUsers: boolean;
    users: IUnitDefaultHandlersNodeUser[];
    selectedUserCodeHandler: string;
    checkedUserCodeHandlers: string[];
    selectedUserCodeNotified: string;
    checkedUserCodeNotifieds: string[];
    sortOrderIsAsc: boolean;
    isRoot: boolean;
    hasSiblings: boolean;
    clearSubUnitHandlers: boolean;
    copyHandlersToAllSameLevelUnits: boolean;
    showSelectUserDialog: boolean;
    selectUserDialogHandlerType: DefaultHandlerType;
}

export class UnitDefaultHandlersEditDialog extends React.Component<IUnitDefaultHandlersEditDialogProp, IUnitDefaultHandlersEditDialogState> {
    private orgStateHash: string = "";

    constructor(props: IUnitDefaultHandlersEditDialogProp) {
        super(props);
        const isRoot = props.unitNode.getIsRoot();
        this.state = {
            hasExplicitUsers: props.unitNode.hasExplicitUsers(),
            users: props.unitNode.getUsers(),
            selectedUserCodeHandler: "",
            checkedUserCodeHandlers: [],
            selectedUserCodeNotified: "",
            checkedUserCodeNotifieds: [],
            sortOrderIsAsc: true,
            isRoot,
            hasSiblings: !isRoot && props.unitNode.hasSiblings(),
            clearSubUnitHandlers: false,
            copyHandlersToAllSameLevelUnits: false,
            showSelectUserDialog: false,
            selectUserDialogHandlerType: DefaultHandlerType.Handler
        };
        const saveData = this.getSaveDataFromState(props, this.state);
        this.orgStateHash = saveData.hash;
    }

    handleSetClearSubUnitHandlers = (clearSubUnitHandlers: boolean) => {
        this.setState({
            clearSubUnitHandlers
        });
    };

    handleSetCopyHandlersToAllSameLevelUnits = (copyHandlersToAllSameLevelUnits: boolean) => {
        this.setState({
            copyHandlersToAllSameLevelUnits
        });
    };

    getSaveDataFromState = (props: IUnitDefaultHandlersEditDialogProp, state: IUnitDefaultHandlersEditDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("hasExplicitUsers", state.hasExplicitUsers);
        result.append("userIds", state.users.map(i => i.code));
        result.append("clearSubUnitHandlers", state.clearSubUnitHandlers);
        result.append("copyHandlersToAllSameLevelUnits", state.copyHandlersToAllSameLevelUnits);
        return result;
    };

    handleRemove = async (defaultHandlerType: DefaultHandlerType) => {
        const state = this.state;
        if (!state.hasExplicitUsers) return;
        const selectedUserCodes = defaultHandlerType === DefaultHandlerType.Handler
            ? List.getSelectedIds(state.selectedUserCodeHandler, state.checkedUserCodeHandlers)
            : List.getSelectedIds(state.selectedUserCodeNotified, state.checkedUserCodeNotifieds);
        if (selectedUserCodes.length < 0) return;
        const users = state.users.filter(i => selectedUserCodes.indexOf(i.code) < 0);
        const hasExplicitUsers = users.length > 0;
        this.setState({
            hasExplicitUsers,
            users: hasExplicitUsers ? users : this.props.unitNode.getUsers()
        });
    };
    
    handleAdd = (selectUserDialogHandlerType: DefaultHandlerType) => {
        this.setState({
            showSelectUserDialog: true,
            selectUserDialogHandlerType
        });
    };

    handleSelectUserDialogOk = async (selectedUsers: IUserListItem[]) => {
        const state = this.state;
        if (!selectedUsers || selectedUsers.length < 1) return;
        const users = state.hasExplicitUsers ? state.users.slice(0) : [];
        for (const selectedUser of selectedUsers) {
            if (users.find(i => i.code === selectedUser.code)) {
                continue;
            }
            const newUser = new UnitDefaultHandlersNodeUser();
            newUser.code = selectedUser.code;
            newUser.name = selectedUser.getName();
            newUser.picture = selectedUser.picture;
            newUser.type = state.selectUserDialogHandlerType;
            users.push(newUser);
        }
        this.setState({
            showSelectUserDialog: false,
            hasExplicitUsers: true,
            users: UnitDefaultHandlersNodeUser.sortUnitDefaultHandlersNodeUsers(users, state.sortOrderIsAsc)
        });
    };

    handleSelectUserDialogCancel = () => {
        this.setState({
            showSelectUserDialog: false
        });
    };

    handleGetUserSelectListItems = (companyId: string, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean,
        _ignoreQrCodeUsers: boolean, _requireQrCodeUsers: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
        const props = this.props;
        return props.onGetUserSelectListItems(companyId, props.unitNode.id, this.state.selectUserDialogHandlerType, pageSize, page, filter,
            ignoreUserIds, ignoreUserCodes, activityState, ignoreRecordUserConnected,
            doNotIgnoreRecordUserConnectedUserCode, sortColumn, sortOrderIsAsc);
    };
    
    handleSetSelectedHandlerIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedUserCodeHandler: selectedId,
            checkedUserCodeHandlers: checkedIds
        });
    };
    
    handleSetSelectedNotifiedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedUserCodeNotified: selectedId,
            checkedUserCodeNotifieds: checkedIds
        });
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.setState({
            sortOrderIsAsc,
            users: UnitDefaultHandlersNodeUser.sortUnitDefaultHandlersNodeUsers(state.users, sortOrderIsAsc)
        });
    };

    save = async () => {
        const state = this.state;
        this.props.onOk(
            state.hasExplicitUsers ? state.users : [],
            state.clearSubUnitHandlers,
            state.copyHandlersToAllSameLevelUnits
        );
    };

    handleSave = () => {
        this.save();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.save();
        } else {
            props.onCancel();
        }
    };

    render() {
        const props = this.props;
        const state = this.state;
        const handlers = props.showHandlerTypes ? state.users.filter(i => i.type === DefaultHandlerType.Handler) : state.users;
        const notified = props.showHandlerTypes ? state.users.filter(i => i.type === DefaultHandlerType.Notfied) : [];
        return (
            <Dialog
                id="unit-default-handlers-edit-dialog"
                maxWidth="sm"
                title={props.unitNode.name}
                body={
                    <Box sx={{ margin: "-1rem 0" }}>
                        <GridRow>
                            <Grid item sm={12}>
                                <ActionContainer
                                    leftItems={
                                        <PanelTitle title={Translations.DefaultHandlers} />
                                    }
                                    rightItems={
                                        <>
                                            <AddUserToolButton
                                                tooltip={Translations.AddDefaultHandler}
                                                onClick={() => this.handleAdd(DefaultHandlerType.Handler)}
                                            />
                                            <RemoveUserToolButton
                                                tooltip={Translations.RemoveDefaultHandler}
                                                disabled={(!state.selectedUserCodeHandler && state.checkedUserCodeHandlers.length < 1) || !state.hasExplicitUsers || state.isRoot && state.users.length < 2}
                                                onClick={() => this.handleRemove(DefaultHandlerType.Handler)}
                                            />
                                        </>
                                    }
                                    margin="0 0 1rem 0"
                                />
                                <List
                                    multiSelect={true}
                                    intialized={true}
                                    isLoading={false}
                                    hasMore={false}
                                    header={{
                                        columns: [
                                            { size: 12, column: "name", content: Translations.Name }
                                        ]
                                    }}
                                    lines={
                                        handlers.map((item) => {
                                            return {
                                                columns: [
                                                    {
                                                        size: 12,
                                                        content:
                                                            <Box display="flex" alignItems="center">
                                                                <AvatarImage
                                                                    id={item.code}
                                                                    name={item.name}
                                                                    image={item.picture}
                                                                    tooltip={item.name}
                                                                />
                                                                <Typography sx={{ marginLeft: "1rem" }}>{item.name}</Typography>
                                                            </Box>
                                                    }
                                                ],
                                                id: item.code,
                                                classes: state.hasExplicitUsers ? "" : "inherited"
                                            };
                                        })
                                    }
                                    sortColumn="name"
                                    sortOrderIsAsc={state.sortOrderIsAsc}
                                    selectedId={state.selectedUserCodeHandler}
                                    checkedIds={state.checkedUserCodeHandlers}
                                    onSetSelectedIds={this.handleSetSelectedHandlerIds}
                                    onSearchLines={this.handleSearchLines}
                                />
                            </Grid>
                            {!!props.showHandlerTypes &&
                                <Grid item sm={12}>
                                    <ActionContainer
                                        leftItems={
                                            <PanelTitle title={Translations.NotifiedPersons} />
                                        }
                                        rightItems={
                                            <>
                                                <AddUserToolButton
                                                    tooltip={Translations.AddNotifiedPerson}
                                                    onClick={() => this.handleAdd(DefaultHandlerType.Notfied)}
                                                />
                                                <RemoveUserToolButton
                                                    tooltip={Translations.RemoveNotifiedPerson}
                                                    disabled={(!state.selectedUserCodeNotified && state.checkedUserCodeNotifieds.length < 1) || !state.hasExplicitUsers || state.isRoot && state.users.length < 2}
                                                    onClick={() => this.handleRemove(DefaultHandlerType.Notfied)}
                                                />
                                            </>
                                        }
                                    />
                                    <List
                                        multiSelect={true}
                                        intialized={true}
                                        isLoading={false}
                                        hasMore={false}
                                        header={{
                                            columns: [
                                                { size: 12, column: "name", content: Translations.Name }
                                            ]
                                        }}
                                        lines={
                                            notified.map((item) => {
                                                return {
                                                    columns: [
                                                        {
                                                            size: 12,
                                                            content:
                                                                <Box display="flex" alignItems="center">
                                                                    <AvatarImage
                                                                        id={item.code}
                                                                        name={item.name}
                                                                        image={item.picture}
                                                                        tooltip={item.name}
                                                                    />
                                                                    <Typography sx={{ marginLeft: "1rem" }}>{item.name}</Typography>
                                                                </Box>
                                                        }
                                                    ],
                                                    id: item.code,
                                                    classes: state.hasExplicitUsers ? "" : "inherited"
                                                };
                                            })
                                        }
                                        sortColumn="name"
                                        sortOrderIsAsc={state.sortOrderIsAsc}
                                        selectedId={state.selectedUserCodeNotified}
                                        checkedIds={state.checkedUserCodeNotifieds}
                                        onSetSelectedIds={this.handleSetSelectedNotifiedIds}
                                        onSearchLines={this.handleSearchLines}
                                    />
                                </Grid>
                            }
                            <Grid item sm={12} sx={{ marginTop: "1rem" }}>
                                {(props.unitNode.hasChildren() || state.hasSiblings) &&
                                    <PanelTitle title={Translations.AdditionalSettings} />
                                }
                                {props.unitNode.hasChildren() &&
                                <FormControlLabel
                                    label={props.showHandlerTypes ? Translations.ClearSubUnitDefaultHandlersAndNotifiedPersons : Translations.ClearSubUnitDefaultHandlers}
                                    control={
                                        <Checkbox
                                            checked={state.clearSubUnitHandlers}
                                            onChange={(_e, value) => this.handleSetClearSubUnitHandlers(value)}
                                        />
                                    }
                                />
                                }
                                {state.hasSiblings &&
                                <FormControlLabel label={props.showHandlerTypes ? Translations.CopyUnitDefaultHandlersAndNotifiedPersonsToAllSameLevelUnits : Translations.CopyDefaultHandlersToAllSameLevelUnits} control={
                                    <Checkbox
                                        checked={state.copyHandlersToAllSameLevelUnits}
                                        onChange={(e, value) => this.handleSetCopyHandlersToAllSameLevelUnits(value)}
                                    />
                                } />
                                }
                            </Grid>
                            {state.showSelectUserDialog &&
                                <SelectUserDialog
                                    companyId={props.companyId}
                                    ignoreUserCodes={state.hasExplicitUsers ? state.users.map(i => i.code) : []}
                                    multiSelect={true}
                                    onOk={this.handleSelectUserDialogOk}
                                    onCancel={this.handleSelectUserDialogCancel}
                                    onGetUserSelectListItems={this.handleGetUserSelectListItems}
                                />
                            }
                        </GridRow>
                    </Box>
                }
                buttons={[
                    { title: Translations.Remove, variant: "danger", onClick: props.unitNode.hasExplicitUsers() && !state.isRoot ? props.onRemove : null },
                    { title: Translations.Close, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.OK, variant: "confirmation", onClick: this.handleSave },
                ]}
                onClose={this.handleCancel}
            />
        );
    }
}